import React, { useEffect, useRef, useState } from "react";

import {
    AppBar,
    Toolbar,
    Typography,
    Box,
    Grid,
    Paper,
    RadioGroup,
    FormControlLabel,
    Radio,
    Button,
    Divider,
    Link,
    useMediaQuery,
    Theme,
    Drawer,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    styled,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardBackspaceOutlinedIcon from '@mui/icons-material/KeyboardBackspaceOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Worker, Viewer, ScrollMode } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { PINNACLE_LOGO_URL } from "../../utils/constants";
import { getExamAttemptDetailsById, initiateExam, submitExam } from "../../api/services/exam";
import { handleApiError } from "../../utils/errorHandler";
import { pushNotification } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { NotificationTypes } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { ExamAttemptDetails, ExamListByCode, InitiateExam, InitiateExamData } from "../../api/interfaces/Exam";
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';

const PDFJS_WORKER_VERSION = "3.11.174";
const APP_BAR_HEIGHT = 64;

const CustomCheckedIcon = styled('div')({
    width: 20,
    height: 20,
    borderRadius: '50%',
    border: '2px solid #028A4F',
    backgroundColor: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
        color: '#028A4F',
        fontSize: 16,
    },
});


const CheckExamPage: React.FC = () => {
    const { examAttemptId } = useParams<{ examAttemptId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [exam, setExam] = useState<ExamAttemptDetails | null>(null);
    // const [attempt, setAttempt] = useState<InitiateExamData | null>(null);
    const [answers, setAnswers] = React.useState<Record<number, string>>({});

    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const toggleDrawer = (open: boolean) => () => setDrawerOpen(open);

    const hasInitiatedExam = React.useRef(false);

    useEffect(() => {
        const init = async () => {
            if (!examAttemptId) return;
            if (hasInitiatedExam.current) return;
            hasInitiatedExam.current = true;

            try {
                setIsLoading(true);
                const getExamAttempt = await getExamAttemptDetailsById(examAttemptId);
                setExam(getExamAttempt.data);

                const apiAnswers = getExamAttempt.data.answers;

                const normalized = Object.keys(apiAnswers).reduce((acc, key) => {
                    const index = Number(key);
                    acc[index + 1] = apiAnswers[key as any];
                    return acc;
                }, {} as Record<number, string>);

                setAnswers(normalized);
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
            } finally {
                setIsLoading(false);
            }
        };
        init();
    }, [examAttemptId]);

    return (
        <>
            {isLoading && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        bgcolor: 'rgba(0,0,0,0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                    }}
                >
                    <CircularProgress color="primary" />
                </Box>
            )}
            {/* MOBILE APP BAR */}
            {isSmall && (
                <AppBar position="static" color="default" elevation={1} sx={{ backgroundColor: "#333533" }}>
                    <Toolbar sx={{ justifyContent: "space-between" }}>
                        {/* Back button + title */}
                        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, flex: 1, overflow: "hidden" }}>
                            <Button
                                sx={{ background: 'transparent', color: 'white', minWidth: 'auto', padding: 0 }}
                                onClick={() => {
                                    navigate(-1);
                                }}
                                aria-label="Back"
                            >
                                <KeyboardBackspaceOutlinedIcon />
                            </Button>

                            <Typography
                                variant="body1"
                                sx={{
                                    fontWeight: 700,
                                    color: "#fff",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    ml: 1,
                                    maxWidth: 'calc(100% - 40px)',
                                }}
                            >
                                {exam?.examName}
                            </Typography>
                        </Box>

                        {/* Answers Key button: prevent stretching */}
                        <Box sx={{ flexShrink: 0, ml: 1 }}>
                            <Button
                                variant="contained"
                                sx={{ bgcolor: "#5840BA", minWidth: 'auto', px: 2 }}
                                startIcon={<InsertDriveFileOutlinedIcon />}
                                onClick={toggleDrawer(true)}
                            >
                                Answers Key
                            </Button>
                        </Box>
                    </Toolbar>
                </AppBar>
            )}

            {/* MAIN CONTENT */}
            <Box
                sx={{
                    display: "flex",
                    height: "100vh",
                    overflow: "hidden",
                }}
            >
                {/* LEFT COLUMN: PDF */}
                <Box
                    component="section"
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        flex: { xs: 1, md: 2 },
                        minWidth: 0,
                        bgcolor: "transparent",
                        overflow: "hidden",
                    }}
                >
                    {!isSmall && (
                        <AppBar position="static" color="default" elevation={1} sx={{ backgroundColor: "#333533" }}>
                            <Toolbar sx={{ justifyContent: "left" }}>
                                <Button
                                    sx={{ background: 'transparent', color: 'white', minWidth: 'auto', padding: 0 }}
                                    aria-label="Back"
                                    onClick={() => navigate(-1)}
                                >
                                    <KeyboardBackspaceOutlinedIcon />
                                </Button>
                                &nbsp;
                                <Typography variant="body1" sx={{ fontWeight: 700, color: '#fff' }}>
                                    {exam?.examName}
                                </Typography>
                            </Toolbar>
                        </AppBar>
                    )}
                    <Box
                        sx={{
                            height: `calc(100vh - ${APP_BAR_HEIGHT}px)`,
                            minHeight: 0,
                            overflow: "hidden",
                        }}
                    >
                        <Worker workerUrl={`https://unpkg.com/pdfjs-dist@${PDFJS_WORKER_VERSION}/build/pdf.worker.min.js`}>
                            <Box
                                sx={{
                                    height: "100%",
                                    overflowY: "auto",
                                }}
                            >
                                {exam?.questionnaire && (
                                    <Viewer
                                        fileUrl={exam.questionnaireWithKeys}
                                        scrollMode={ScrollMode.Vertical}
                                    />
                                )}
                            </Box>
                        </Worker>
                    </Box>
                </Box>

                {/* RIGHT COLUMN: Desktop only */}
                {!isSmall && (
                    <Box
                        sx={{
                            position: "sticky",
                            top: 0,
                            height: "100vh",
                            width: "35%",
                            minWidth: 0,
                            flexShrink: 0,
                        }}
                    >
                        <Paper
                            sx={{
                                p: 3,
                                display: "flex",
                                flexDirection: "column",
                                height: "100%",
                                overflow: "hidden",
                            }}
                        >
                            {/* Top: Logo + Attempt No + Timer */}
                            <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between" }}>
                                <Box component="img" src={PINNACLE_LOGO_URL} alt="Logo" sx={{ height: 40 }} />
                                {exam && (
                                    <Typography sx={{ alignContent: 'flex-end', color: "#5840BA", fontSize: "12px", fontWeight: 500 }}>
                                        Attempt No: {exam.attemptNumber} of {exam.maxAttempts}
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between" }}>
                                {exam && (
                                    <>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }} >{exam && exam.score ? 'Answer key with correction' : 'Answers Key'}</Typography>
                                        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                                            <Typography variant="body2" sx={{ fontWeight: 500, alignSelf: "center", color: "#818181" }} >
                                                Score: {exam?.score !== null ? exam?.score : 'TBA'} / {exam?.numberOfQuestions}
                                            </Typography>
                                        </Box>
                                    </>

                                )}
                            </Box>

                            {/* Scrollable questions */}
                            <Box sx={{ flex: 1, overflowY: "auto", pr: 1, minHeight: 0 }}>
                                {exam && (
                                    <Questions
                                        score={exam.score}
                                        answerKey={exam.answerKey}
                                        numQuestions={exam?.numberOfQuestions}
                                        answers={answers}
                                        setAnswers={setAnswers}
                                    />
                                )}
                            </Box>
                        </Paper>
                    </Box>
                )}
            </Box>

            {/* MOBILE DRAWER */}
            {isSmall && (
                <Drawer
                    anchor="right"
                    open={drawerOpen}
                    onClose={toggleDrawer(false)}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    PaperProps={{
                        sx: {
                            top: '56px',
                            height: `calc(100% - 56px)`,
                            width: '90vw',
                            maxWidth: 360,
                        },
                    }}
                >
                    <Box sx={{ p: '16px', display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
                        <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between" }}>
                            <Button
                                sx={{ minWidth: 'auto', padding: 0 }}
                                onClick={toggleDrawer(false)}
                                aria-label="Back"
                            >
                                <KeyboardBackspaceOutlinedIcon sx={{ color: "#373737" }} />
                            </Button>
                            <Box component="img" src={PINNACLE_LOGO_URL} alt="Logo" sx={{ height: 25 }} />
                            <Typography sx={{ alignContent: 'center', color: "#5840BA", fontSize: "12px", fontWeight: 500 }}>
                                Attempt No: {exam?.attemptNumber} of {exam?.maxAttempts}
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }} >{exam && exam.score ? 'Answer key with correction' : 'Answers Key'}</Typography>
                            <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                                <Typography variant="body2" sx={{ fontWeight: 400, alignSelf: "center", color: "#818181" }} >
                                    Score: {exam?.score !== null ? exam?.score : 'TBA'} / {exam?.numberOfQuestions}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ flex: 1, overflowY: "auto", pr: 1, minHeight: 0 }}>
                            {exam && (
                                <Questions
                                    score={exam.score}
                                    answerKey={exam.answerKey}
                                    numQuestions={exam?.numberOfQuestions}
                                    answers={answers}
                                    setAnswers={setAnswers}
                                />
                            )}
                        </Box>
                    </Box>
                </Drawer>
            )}
        </>
    );
};

interface QuestionsProps {
    score: null | number | string;
    answerKey: string[];
    numQuestions: number;
    answers: Record<number, string>;
    setAnswers: React.Dispatch<React.SetStateAction<Record<number, string>>>;
}

const Questions: React.FC<QuestionsProps> = ({
    score,
    answerKey,
    numQuestions,
    answers,
}) => {
    return (
        <>
            {Array.from({ length: numQuestions }).map((_, qIndex) => {
                const qNum = qIndex + 1;
                const userAnswer = answers[qNum];
                const correctAnswer = answerKey[qIndex];

                const hasAnswer = userAnswer !== undefined && userAnswer !== null;
                const isCorrect = hasAnswer && userAnswer === correctAnswer;
                const isWrong = hasAnswer && userAnswer !== correctAnswer;
                const isBonus = correctAnswer === "BONUS";

                const showCorrection = score !== null && (!!correctAnswer || isBonus);

                return (
                    <Box key={qNum} sx={{ mb: 2 }}>
                        <RadioGroup
                            name={`q-${qNum}`}
                            value={userAnswer ?? ""}
                        >
                            {/* Question row (ICON + NUMBER + OPTION A) */}
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "20px 28px auto",
                                    alignItems: "center",
                                }}
                            >
                                {/* Icon column */}
                                <Box sx={{ textAlign: "center", display: 'flex', gap: 1 }}>
                                    {showCorrection && (
                                        isBonus ? (
                                            <CheckOutlinedIcon sx={{ color: "#028A4F", fontSize: 16 }} />
                                        ) : isCorrect ? (
                                            <CheckOutlinedIcon sx={{ color: "#2E7D32", fontSize: 16 }} />
                                        ) : (
                                            <ClearOutlinedIcon sx={{ color: "#D32F2F", fontSize: 16 }} />
                                        )
                                    )}
                                </Box>

                                {/* Question number */}
                                <Typography
                                    variant="body1"
                                    fontWeight={700}
                                >
                                    {qNum}.
                                </Typography>

                                {/* Choice A */}
                                <FormControlLabel
                                    value="A"
                                    control={
                                        <Radio
                                            size="small"
                                            checked={userAnswer === "A" || (isWrong && correctAnswer === "A")}
                                            sx={{
                                                color:
                                                    isWrong && correctAnswer === "A"
                                                        ? "#028A4F"
                                                        : "inherit",
                                                "&.Mui-checked": {
                                                    color:
                                                        isWrong && correctAnswer === "A"
                                                            ? "#028A4F"
                                                            : "#5840BA",
                                                },
                                            }}
                                            icon={
                                                <div
                                                    style={{
                                                        width: 20,
                                                        height: 20,
                                                        borderRadius: "50%",
                                                        border: "2px solid #999",
                                                        backgroundColor: "white",
                                                    }}
                                                />
                                            }
                                            // Only show the custom green check if it's the correct answer
                                            checkedIcon={
                                                correctAnswer === "A"
                                                    ? <CustomCheckedIcon><CheckIcon /></CustomCheckedIcon>
                                                    : undefined
                                            }
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <Typography
                                                sx={{
                                                    color:
                                                        !isBonus && isWrong && correctAnswer === "A"
                                                            ? "#028A4F"
                                                            : "inherit",
                                                    fontWeight: 400,
                                                    textTransform: "lowercase",
                                                }}
                                            >
                                                a.
                                            </Typography>

                                            {isBonus && (
                                                <Typography
                                                    sx={{
                                                        color: "#028A4F",
                                                        fontWeight: 400,
                                                    }}
                                                >
                                                    BONUS
                                                </Typography>
                                            )}
                                        </Box>
                                    }
                                />

                            </Box>

                            {/* Choices B–D */}
                            {["B", "C", "D"].map((choice) => (
                                <Box
                                    key={choice}
                                    sx={{
                                        ml: "48px",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <FormControlLabel
                                        value={choice}
                                        control={
                                            <Radio
                                                size="small"
                                                checked={
                                                    userAnswer === choice ||
                                                    (!isBonus && showCorrection && correctAnswer === choice)
                                                }
                                                sx={{
                                                    color:
                                                        isWrong && correctAnswer === choice
                                                            ? "#028A4F"
                                                            : "inherit",
                                                    "&.Mui-checked": {
                                                        color:
                                                            isWrong && correctAnswer === choice
                                                                ? "#028A4F"
                                                                : "#5840BA",
                                                    },
                                                }}
                                                icon={
                                                    <div
                                                        style={{
                                                            width: 20,
                                                            height: 20,
                                                            borderRadius: "50%",
                                                            border: "2px solid #999",
                                                            backgroundColor: "white",
                                                        }}
                                                    />
                                                }
                                                checkedIcon={
                                                    showCorrection &&
                                                        !isBonus &&
                                                        !isCorrect &&
                                                        correctAnswer === choice
                                                        ? (
                                                            <CustomCheckedIcon>
                                                                <CheckIcon />
                                                            </CustomCheckedIcon>
                                                        )
                                                        : undefined
                                                }
                                            />
                                        }
                                        label={
                                            <Typography
                                                sx={{
                                                    color:
                                                        isWrong && correctAnswer === choice
                                                            ? "#028A4F"
                                                            : "inherit",
                                                    fontWeight: 400,
                                                    textTransform: "lowercase",
                                                }}
                                            >
                                                {`${choice.toLowerCase()}.`}
                                            </Typography>
                                        }
                                    />
                                </Box>
                            ))}
                        </RadioGroup>
                    </Box>
                );
            })}
        </>
    );
};


export default CheckExamPage;
